import {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Text,
    Badge,
    SimpleGrid,
    Image,
    Flex,
    Grid,
    Dialog,
    Button,
    Input,
} from "@chakra-ui/react";

import {
    Pencil,
    Save,
    X,
} from "lucide-react";

import PdfIcon from "../../../assets/pdf.png";
import AppDrawer from "../../../components/AppDrawer";

const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

const ViewDriverDrawer = ({
    driver,
    open,
    onClose,
    onEdit,
    onUpdate,
}) => {

    const [
        selectedMedia,
        setSelectedMedia,
    ] = useState(null);

    const [
        isEditing,
        setIsEditing,
    ] = useState(false);

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        form,
        setForm,
    ] = useState({});


    useEffect(() => {

        if (!driver) {
            return;
        }

        setForm({
            license_number:
                driver.license_number || "",

            license_expiry_date:
                driver.license_expiry_date || "",

            route_preference:
                driver.route_preference || "",

            package_preference:
                driver.package_preference || "",

            driver_license: null,
            criminal_record: null,
            identity_proof: null,
            police_affidavit: null,
            residential_address_proof: null,
            profile_image: null,
        });

        setIsEditing(false);

    }, [driver]);


    if (!driver) {
        return null;
    }


    const user =
        driver.user ||
        driver;


    const getStatusColor =
        status => {

            switch (status) {

                case "Approved":
                    return "green";

                case "Pending":
                    return "orange";

                case "Rejected":
                    return "red";

                case "Suspended":
                    return "red";

                case "Active":
                    return "blue";

                case "Inactive":
                    return "yellow";
                case "Review Required":
                    return "orange";

                default:
                    return "gray";
            }
        };


    const driverDocuments =
        driver.documents?.filter(
            doc =>
                ![
                    "Blue Book",
                    "Vehicle Image",
                ].includes(
                    doc.document_type
                )
        ) || [];


    const profileImage =
        driverDocuments.find(
            doc =>
                doc.document_type ===
                "Profile Image"
        );


    const fullName =
        [
            user.first_name,
            user.middle_name,
            user.last_name,
        ]
            .filter(Boolean)
            .join(" ") ||
        user.username ||
        "Driver";


    const fieldMap = {
        "Driver License":
            "driver_license",

        "Criminal Record":
            "criminal_record",

        "Identity Proof":
            "identity_proof",

        "Police Affidavit":
            "police_affidavit",

        "Residential Address Proof":
            "residential_address_proof",

        "Profile Image":
            "profile_image",
    };


    const handleChange =
        (
            field,
            value
        ) => {

            setForm(
                previous => ({
                    ...previous,
                    [field]: value,
                })
            );
        };


const handleReplace = (
    doc,
    file
) => {
    if (!file) {
        return;
    }

    const field =
        fieldMap[
            doc.document_type
        ];

    if (!field) {
        return;
    }

    setForm(previous => ({
        ...previous,
        [field]: [
            {
                file,
            },
        ],
    }));
};

    const getDocumentFile =
        doc => {

            const field =
                fieldMap[
                    doc.document_type
                ];

            return field
                ? form[field]
                : null;
        };


const getDocumentPreview = (doc) => {
    const newFile = getDocumentFile(doc);

    const file =
        newFile?.[0]?.file ||
        newFile ||
        null;

    if (file) {
        return {
            src: URL.createObjectURL(file),
            extension: file.name
                ?.split(".")
                .pop()
                ?.toLowerCase(),
            isNew: true,
        };
    }

    return {
        src: `${BASE_URL}/${doc.file_path}`,
        extension: doc.file_extension
            ?.toLowerCase(),
        isNew: false,
    };
};

    const handleEdit =
        () => {

            if (onEdit) {
                onEdit(driver);
            }

            setIsEditing(true);
        };


    const handleCancel =
        () => {

            setForm({
                license_number:
                    driver.license_number || "",

                license_expiry_date:
                    driver.license_expiry_date || "",

                route_preference:
                    driver.route_preference || "",

                package_preference:
                    driver.package_preference || "",

                driver_license: null,
                criminal_record: null,
                identity_proof: null,
                police_affidavit: null,
                residential_address_proof: null,
                profile_image: null,
            });

            setIsEditing(false);
        };


    const handleSave =
        async () => {

            if (!onUpdate) {
                return;
            }

            try {

                setSaving(true);

                const result =
                    await onUpdate(
                        driver,
                        form
                    );

                if (
                    result?.success
                ) {
                    setIsEditing(false);
                }

            } finally {

                setSaving(false);
            }
        };


    return (
        <>
            <AppDrawer
                open={open}
                onClose={onClose}
                title="Driver Details"
                width="600px"
            >

                <Box mb={40}>

                    {/* HEADER */}

                    <Flex
                        direction={{
                            base: "column",
                            md: "row",
                        }}
                        align={{
                            base: "stretch",
                            md: "center",
                        }}
                        justify="space-between"
                        gap={4}
                        mb={6}
                    >

                        <Box minW={0}>

                            <Text
                                fontSize="24px"
                                fontWeight="700"
                                color="#1E293B"
                                wordBreak="break-word"
                            >
                                {fullName}
                            </Text>

                            <Text
                                color="gray.500"
                                fontSize="sm"
                                wordBreak="break-word"
                            >
                                {user.email}
                            </Text>

                            <Flex
                                gap={2}
                                mt={2}
                                flexWrap="wrap"
                            >
                                <Badge
                                    colorPalette={
                                        getStatusColor(
                                            driver.status
                                        )
                                    }
                                >
                                    {driver.status ||
                                        "Not Submitted"}
                                </Badge>
                            </Flex>

                        </Box>


                        {driver.driver_id && (

                            <Flex
                                gap={2}
                                flexShrink={0}
                                alignSelf={{
                                    base: "flex-start",
                                    md: "center",
                                }}
                            >

                                {!isEditing ? (

                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={
                                            handleEdit
                                        }
                                    >
                                        <Pencil
                                            size={15}
                                        />
                                        Edit Driver
                                    </Button>

                                ) : (

                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={
                                                handleCancel
                                            }
                                        >
                                            <X
                                                size={15}
                                            />
                                            Cancel
                                        </Button>

                                        <Button
                                            size="sm"
                                            colorPalette="blue"
                                            loading={
                                                saving
                                            }
                                            onClick={
                                                handleSave
                                            }
                                        >
                                            <Save
                                                size={15}
                                            />
                                            Save
                                        </Button>
                                    </>

                                )}

                            </Flex>

                        )}

                    </Flex>


                    {/* DRIVER INFORMATION */}

                    <Text
                        fontSize="11px"
                        fontWeight="600"
                        color="#64748B"
                        textTransform="uppercase"
                        mb={4}
                    >
                        Driver Information
                    </Text>


                    <Grid
                        templateColumns={{
                            base: "1fr",
                            md: "repeat(2, 1fr)",
                        }}
                        gap={4}
                        mb={8}
                    >

                        <Box>
                            <Text
                                fontSize="12px"
                                color="gray.500"
                            >
                                Username
                            </Text>

                            <Text fontWeight="600">
                                {user.username || "-"}
                            </Text>
                        </Box>


                        <Box>
                            <Text
                                fontSize="12px"
                                color="gray.500"
                            >
                                Phone
                            </Text>

                            <Text fontWeight="600">
                                {
                                    user.phone_number ||
                                    "-"
                                }
                            </Text>
                        </Box>


                        <Box>
                            <Text
                                fontSize="12px"
                                color="gray.500"
                            >
                                District
                            </Text>

                            <Text fontWeight="600">
                                {user.district || "-"}
                            </Text>
                        </Box>


                        <Box>
                            <Text
                                fontSize="12px"
                                color="gray.500"
                            >
                                Village
                            </Text>

                            <Text fontWeight="600">
                                {user.village || "-"}
                            </Text>
                        </Box>


                        {driver.driver_id && (
                            <>

                                <Box>
                                    <Text
                                        fontSize="12px"
                                        color="gray.500"
                                        mb={
                                            isEditing
                                                ? 1
                                                : 0
                                        }
                                    >
                                        License Number
                                    </Text>

                                    {isEditing ? (

                                        <Input
                                            size="sm"
                                            value={
                                                form.license_number ||
                                                ""
                                            }
                                            onChange={e =>
                                                handleChange(
                                                    "license_number",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text fontWeight="600">
                                            {
                                                driver.license_number ||
                                                "-"
                                            }
                                        </Text>

                                    )}

                                </Box>


                                <Box>
                                    <Text
                                        fontSize="12px"
                                        color="gray.500"
                                        mb={
                                            isEditing
                                                ? 1
                                                : 0
                                        }
                                    >
                                        License Expiry
                                    </Text>

                                    {isEditing ? (

                                        <Input
                                            size="sm"
                                            type="date"
                                            value={
                                                form.license_expiry_date
                                                    ? String(
                                                        form.license_expiry_date
                                                    ).substring(
                                                        0,
                                                        10
                                                    )
                                                    : ""
                                            }
                                            onChange={e =>
                                                handleChange(
                                                    "license_expiry_date",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text fontWeight="600">
                                            {
                                                driver.license_expiry_date ||
                                                "-"
                                            }
                                        </Text>

                                    )}

                                </Box>


                              <Box>
    <Text
        fontSize="12px"
        color="gray.500"
        mb={1}
    >
        Route Preference
    </Text>

    {isEditing ? (
        <select
            value={
                form.route_preference || ""
            }
            onChange={(e) =>
                handleChange(
                    "route_preference",
                    e.target.value
                )
            }
            style={{
                width: "100%",
                padding: "10px",
                borderRadius: "3px",
                border:
                    "1px solid #E2E8F0",
                background: "white",
            }}
        >
            <option value="">
                Select route preference
            </option>

            <option value="Short Distance">
                Short Distance
            </option>

            <option value="Long Distance">
                Long Distance
            </option>
        </select>
    ) : (
        <Text fontWeight="600">
            {driver.route_preference || "-"}
        </Text>
    )}
</Box>


                              <Box>
    <Text
        fontSize="12px"
        color="gray.500"
        mb={1}
    >
        Package Preference
    </Text>

    {isEditing ? (
        <select
            value={
                form.package_preference || ""
            }
            onChange={(e) =>
                handleChange(
                    "package_preference",
                    e.target.value
                )
            }
            style={{
                width: "100%",
                padding: "10px",
                borderRadius: "3px",
                border:
                    "1px solid #E2E8F0",
                background: "white",
            }}
        >
            <option value="">
                Select package preference
            </option>

            <option value="Small">
                Small
            </option>

            <option value="Medium">
                Medium
            </option>

            <option value="Large">
                Large
            </option>
        </select>
    ) : (
        <Text fontWeight="600">
            {driver.package_preference || "-"}
        </Text>
    )}
</Box>

                            </>
                        )}

                    </Grid>


                    {/* DOCUMENTS */}

                    {driverDocuments.length > 0 && (

                        <>

                            <Text
                                fontSize="11px"
                                fontWeight="600"
                                color="#64748B"
                                textTransform="uppercase"
                                mb={3}
                            >
                                Documents
                            </Text>


                            <SimpleGrid
                                columns={{
                                    base: 1,
                                    md: 2,
                                }}
                                gap={3}
                            >

                                {driverDocuments.map(
                                    doc => {

                                        const preview =
                                            getDocumentPreview(
                                                doc
                                            );

                                        const isImage =
                                            [
                                                "png",
                                                "jpg",
                                                "jpeg",
                                                "webp",
                                            ].includes(
                                                preview.extension
                                            );


                                        return (
                                            <Box
                                                key={
                                                    doc.document_id
                                                }
                                                border="1px solid"
                                                borderColor="#E2E8F0"
                                                borderRadius="10px"
                                                overflow="hidden"
                                            >

                                                {/* PREVIEW */}

                                                <Box
                                                    cursor="pointer"
                                                    onClick={() =>
                                                        setSelectedMedia({
                                                            src:
                                                                preview.src,

                                                            title:
                                                                doc.document_type,

                                                            extension:
                                                                preview.extension,
                                                        })
                                                    }
                                                >

                                                    {isImage ? (

                                                        <Image
                                                            src={
                                                                preview.src
                                                            }
                                                            h="180px"
                                                            w="100%"
                                                            objectFit="cover"
                                                        />

                                                    ) : (

                                                        <Flex
                                                            h="180px"
                                                            justify="center"
                                                            align="center"
                                                            bg="gray.50"
                                                        >
                                                            <Image
                                                                src={
                                                                    PdfIcon
                                                                }
                                                                h="80%"
                                                                w="80%"
                                                                objectFit="contain"
                                                            />
                                                        </Flex>

                                                    )}

                                                </Box>


                                                <Box p={3}>

                                                    <Text
                                                        fontWeight="600"
                                                        fontSize="12px"
                                                    >
                                                        {
                                                            doc.document_type
                                                        }
                                                    </Text>

<Text
    fontSize="11px"
    color="gray.500"
    wordBreak="break-all"
>
    {
        getDocumentFile(doc)?.[0]?.file?.name ||
        doc.file_path?.split("/").pop()
    }
</Text>


                                                    {isEditing && (

                                                        <Button
                                                            mt={2}
                                                            size="xs"
                                                            variant="ghost"
                                                            color="#2453FF"
                                                            as="label"
                                                            cursor="pointer"
                                                            onClick={e =>
                                                                e.stopPropagation()
                                                            }
                                                        >

                                                            <Pencil
                                                                size={13}
                                                            />

                                                            Replace

                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept={
                                                                    doc.document_type ===
                                                                    "Profile Image"
                                                                        ? ".jpg,.jpeg,.png,.webp"
                                                                        : ".jpg,.jpeg,.png,.pdf"
                                                                }
                                                                onChange={e => {

                                                                    handleReplace(
                                                                        doc,
                                                                        e.target.files?.[0]
                                                                    );

                                                                    e.target.value =
                                                                        "";
                                                                }}
                                                            />

                                                        </Button>

                                                    )}

                                                </Box>

                                            </Box>
                                        );
                                    }
                                )}

                            </SimpleGrid>

                        </>

                    )}

                </Box>

            </AppDrawer>


            {/* DOCUMENT PREVIEW MODAL */}

            <Dialog.Root
                open={
                    !!selectedMedia
                }
                onOpenChange={details => {

                    if (!details.open) {
                        setSelectedMedia(null);
                    }

                }}
            >

                <Dialog.Backdrop />

                <Dialog.Positioner
                    p={4}
                >

                    <Dialog.Content
                        w="100%"
                        maxW="800px"
                        maxH="90vh"
                        overflow="hidden"
                    >

                        <Dialog.Header>

                            <Dialog.Title>
                                {
                                    selectedMedia?.title
                                }
                            </Dialog.Title>

                        </Dialog.Header>


                        <Dialog.Body
                            p={0}
                        >

                            {[
                                "png",
                                "jpg",
                                "jpeg",
                                "webp",
                            ].includes(
                                selectedMedia?.extension
                                    ?.toLowerCase()
                            ) ? (

                                <Image
                                    src={
                                        selectedMedia?.src
                                    }
                                    w="100%"
                                    maxH="80vh"
                                    objectFit="contain"
                                />

                            ) : (

                                <Box
                                    h="80vh"
                                >
                                    <iframe
                                        src={
                                            selectedMedia?.src
                                        }
                                        title={
                                            selectedMedia?.title
                                        }
                                        style={{
                                            width:
                                                "100%",
                                            height:
                                                "100%",
                                            border:
                                                "none",
                                        }}
                                    />
                                </Box>

                            )}

                        </Dialog.Body>

                    </Dialog.Content>

                </Dialog.Positioner>

            </Dialog.Root>

        </>
    );
};

export default ViewDriverDrawer;